/**
 * The revision list. One row per formal issue, newest first.
 *
 * Every row says which of the three notions it satisfies, so the reader can see
 * the whole picture rather than trusting a single highlighted row — and a
 * superseded revision is visibly superseded, because the cost of mistaking one
 * for current is a site team building the wrong thing.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, FileCheck2, HardHat, Layers, Loader2, MessageSquare, Paperclip, RefreshCw, Send, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { day } from '@/components/nfa/nfaTheme';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** The flattened commented copy — a RENDITION, labelled as one.
 *
 * It is generated at close-out from the stored mark-up geometry, which means it can be
 * regenerated at any time and can never be the record. Surfacing it without that label
 * would invite somebody to transmit it as the issued document.
 */
const CommentedCopy = ({ t, revision }) => {
  const existing = (revision.files || []).find((f) => f.subtype === 'commented_copy');
  const [rec, setRec] = useState(existing || null);
  const [busy, setBusy] = useState(false);
  const closedOut = !!revision.status_code;

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/edm/revisions/${revision.revision_id}/commented-copy`);
      setRec(r.data.file);
      toast.success('Commented copy regenerated from the stored mark-up geometry');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.why || d?.error || 'Could not generate it'));
    } finally { setBusy(false); }
  };

  if (!rec && !closedOut) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5"
      data-testid={`revision-commented-copy-${revision.revision_code}`}
      style={{ border: `1px dashed ${t.border}`, background: t.tint ? t.tint(t.primary, '10') : 'transparent' }}>
      <FileCheck2 size={12} style={{ color: t.primary }} />
      {rec ? (
        <>
          <a href={`${API_BASE}/api/edm/files/${rec.file_id}/download`}
            data-testid={`revision-commented-copy-download-${revision.revision_code}`}
            className="font-mono text-[10px] underline" style={{ color: t.primary }}>
            {rec.filename}
          </a>
          <span className="text-[10px]" style={{ color: t.muted }}>
            {rec.pages ? `${rec.pages} page(s) · ` : ''}rendition, not the issued document
          </span>
        </>
      ) : (
        <span className="text-[10px]" style={{ color: t.muted }}>
          This revision is closed out but no commented copy has been generated.
        </span>
      )}
      <button type="button" onClick={regenerate} disabled={busy}
        data-testid={`revision-commented-copy-regenerate-${revision.revision_code}`}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px]"
        style={{ border: `1px solid ${t.border}`, color: t.body }}>
        {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
        {rec ? 'regenerate' : 'generate'}
      </button>
      {rec?.statement && (
        <span className="text-[9.5px] w-full" style={{ color: t.muted }}>{rec.statement}</span>
      )}
    </div>
  );
};

const NOTION_CHIP = {
  LATEST: { label: 'Latest', icon: Layers, tone: 'primary' },
  LATEST_APPROVED: { label: 'Latest approved', icon: CheckCircle2, tone: 'primary' },
  CURRENT_FOR_CONSTRUCTION: { label: 'Current for construction', icon: HardHat, tone: 'green' },
};

const Flag = ({ t, on, children }) => (
  <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
    style={{
      background: on ? t.tint(t.green, '26') : 'transparent',
      color: on ? t.green : t.muted,
      border: `1px solid ${on ? t.tint(t.green, '55') : t.border}`,
      opacity: on ? 1 : 0.5,
    }}>
    {on ? '✓ ' : '✕ '}{children}
  </span>
);

export const EdmRevisionList = ({ t, revisions = [], onUpload, uploading }) => {
  const rows = [...revisions].sort((a, b) => b.sequence - a.sequence);
  return (
    <section className="mt-8" data-testid="edm-revision-list">
      <div className="flex items-center justify-between">
        <h2 className="text-base md:text-lg font-semibold" style={{ color: t.ink }}>
          Revision history
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-wider"
          style={{ color: t.muted }} data-testid="revision-count">
          {rows.length} revision{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-3 overflow-hidden rounded-xl"
        style={{ border: `1px solid ${t.border}`, background: t.surface }}>
        {rows.length === 0 && (
          <div className="p-6 text-sm" style={{ color: t.muted }} data-testid="revisions-empty">
            No revision has been opened on this document yet.
          </div>
        )}
        {rows.map((r, i) => {
          const notions = r.notions || [];
          const f = r.flags || {};
          const tr = r.transmittal;
          return (
            <div
              key={r.revision_id}
              data-testid={`revision-row-${r.revision_code}`}
              className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-start lg:gap-6"
              style={{
                borderTop: i === 0 ? 'none' : `1px solid ${t.border}`,
                background: notions.length ? t.tint(t.primary, '0A') : 'transparent',
              }}
            >
              {/* code + sequence */}
              <div className="flex w-full shrink-0 items-baseline gap-3 lg:w-40">
                <span className="font-mono text-2xl font-bold tracking-tight"
                  style={{
                    color: r.superseded ? t.muted : t.ink,
                    textDecoration: r.superseded ? 'line-through' : 'none',
                    textDecorationColor: t.tint(t.muted, '88'),
                  }}>
                  {r.revision_code}
                </span>
                <span className="font-mono text-[10px]" style={{ color: t.muted }}>
                  seq {r.sequence}
                  {r.series_flipped_here && ' · series flipped here'}
                </span>
              </div>

              {/* status + flags */}
              <div className="w-full shrink-0 lg:w-56">
                <div className="font-mono text-[11px] px-2 py-1 inline-block rounded"
                  style={{
                    background: t.tint(f.counts_as_approved ? t.green : t.amber, '1A'),
                    color: f.counts_as_approved ? t.green : t.amber,
                  }}
                  data-testid={`revision-status-${r.revision_code}`}>
                  {r.status_code ? `${r.status_code} · ${r.statusLabel || ''}`
                    : 'no status — in review'}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <Flag t={t} on={f.counts_as_approved}>approved</Flag>
                  <Flag t={t} on={f.permits_construction}>build</Flag>
                  <Flag t={t} on={f.requires_resubmission}>resubmit</Flag>
                </div>
                {r.statusMeaning && (
                  <div className="mt-1.5 text-[10px] leading-snug" style={{ color: t.muted }}>
                    {r.statusMeaning}
                  </div>
                )}
              </div>

              {/* reason, transmittal, counts */}
              <div className="min-w-0 flex-1">
                <div className="text-sm" style={{ color: t.ink }}>
                  {r.reason_for_issue || '—'}
                </div>
                <div className="mt-1 font-mono text-[10px]" style={{ color: t.muted }}>
                  {r.issue_date ? `issued ${day(r.issue_date)}` : 'not yet issued'}
                  {r.created_by_name && ` · opened by ${r.created_by_name}`}
                  {r.code_recommended_by_name
                    && ` · code recommended by ${r.code_recommended_by_name}`}
                  {r.closed_out_by_name && ` · closed out by ${r.closed_out_by_name}`}
                  {r.closed_out_under_admin_exception && ' (admin exception)'}
                </div>

                {tr && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px]"
                    data-testid={`revision-transmittal-${r.revision_code}`}
                    style={{ color: tr.acknowledged ? t.body : t.amber }}>
                    <Send size={12} className="mt-0.5 shrink-0" />
                    <span>
                      {tr.transmittal_no} to {tr.to_org} — {tr.statement}
                    </span>
                  </div>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px]"
                  style={{ color: t.muted }}>
                  <span className="inline-flex items-center gap-1">
                    <Paperclip size={11} />{(r.files || []).length} file(s)
                    {r.versionCount > 1 && ` · ${r.versionCount} uploads`}
                  </span>
                  <span className="inline-flex items-center gap-1"
                    style={{ color: r.openComments ? t.amber : t.muted }}
                    data-testid={`revision-comments-${r.revision_code}`}>
                    <MessageSquare size={11} />
                    {r.commentCount || 0} comment(s)
                    {r.openComments ? ` · ${r.openComments} undispositioned` : ''}
                  </span>
                  <a href={`/engineering/revisions/${r.revision_id}/viewer`}
                    data-testid={`revision-open-viewer-${r.revision_code}`}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5"
                    style={{ border: `1px solid ${t.border}`, color: t.primary }}>
                    <MessageSquare size={11} /> open viewer &amp; mark up
                  </a>
                  {onUpload && (
                    <label className="inline-flex cursor-pointer items-center gap-1 rounded px-1.5 py-0.5"
                      data-testid={`revision-upload-${r.revision_code}`}
                      style={{ border: `1px solid ${t.border}`, color: t.primary }}>
                      <Upload size={11} />
                      {uploading === r.revision_id ? 'uploading…' : 'upload file'}
                      <input type="file" className="hidden"
                        onChange={(e) => { onUpload(r.revision_id, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>

                {(r.files || []).filter((f) => f.subtype !== 'commented_copy').length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2"
                    data-testid={`revision-files-${r.revision_code}`}>
                    {(r.files || []).filter((f) => f.subtype !== 'commented_copy').map((f) => (
                      <span key={f.file_id} className="inline-flex items-center gap-1">
                        <a href={`${API_BASE}/api/edm/files/${f.file_id}/download`}
                          data-testid={`revision-file-${f.file_id}`}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px]"
                          style={{ border: `1px solid ${t.border}`, color: t.body }}>
                          <Paperclip size={10} />{f.filename}
                          <span style={{ color: t.muted }}>· {f.role}</span>
                        </a>
                        {f.integrity && f.integrity.plausible === false && (
                          <span data-testid={`revision-file-flagged-${f.file_id}`}
                            title={f.integrity.reason}
                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                            style={{ background: t.tint(t.red, '1F'), color: t.red,
                              border: `1px solid ${t.tint(t.red, '55')}` }}>
                            <AlertTriangle size={9} /> not a readable {f.integrity.format}
                          </span>
                        )}
                        {f.aps_translation_status === 'not_submitted' && (
                          <span data-testid={`revision-file-not-translated-${f.file_id}`}
                            title={f.aps_translation_detail}
                            className="font-mono text-[9px] uppercase tracking-wider"
                            style={{ color: t.amber }}>not sent for conversion</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                <CommentedCopy t={t} revision={r} />
              </div>

              {/* notion chips */}
              <div className="flex w-full shrink-0 flex-col gap-1 lg:w-52 lg:items-end">
                {notions.map((n) => {
                  const spec = NOTION_CHIP[n];
                  if (!spec) return null;
                  const Icon = spec.icon;
                  const col = spec.tone === 'green' ? t.green : t.primary;
                  return (
                    <span key={n} data-testid={`revision-notion-${r.revision_code}-${n}`}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                                 font-mono text-[10px] uppercase tracking-wider"
                      style={{
                        background: t.tint(col, '1F'), color: col,
                        border: `1px solid ${t.tint(col, '44')}`,
                      }}>
                      <Icon size={10} />{spec.label}
                    </span>
                  );
                })}
                {!notions.length && (
                  <span className="font-mono text-[10px] uppercase tracking-wider"
                    style={{ color: t.muted }}
                    data-testid={`revision-superseded-${r.revision_code}`}>
                    superseded
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[11px]" style={{ color: t.muted }}>
        No row is flagged as current in the database. The chips on the right are worked out
        from the revision sequence, the status-code flags and the transmittal
        acknowledgements each time this page is opened.
      </p>
    </section>
  );
};

export default EdmRevisionList;
